import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CB, F, S, Section, T, commaArray, optEmail, optInt, optNum, optPhone, optStr, phone } from "@/components/FormFields";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONDITIONS, ENQUIRY_FOR, FACING, FURNISHING, LEAD_PRIORITIES, LEAD_STATUSES, PROPERTY_TYPES, SUBTYPES } from "@/lib/constants";
import { PageHeader } from "@/pages/PlaceholderPage";

const schema = z
  .object({
    enquiry_for: z.string().min(1, "Required"),
    property_type_interested: z.string().min(1, "Required"),
    property_subtype_interested: optStr,
    customer_name: z.string().min(1, "Customer name is required"),
    customer_address: optStr,
    customer_phone_primary: phone,
    customer_phone_secondary: optPhone,
    customer_email: optEmail,
    customer_dob: optStr,
    customer_occupation: optStr,
    preferred_areas: optStr,
    preferred_streets: optStr,
    city: z.string().min(1, "City is required"),
    saleable_area_min: optNum(), saleable_area_max: optNum(),
    plot_area_min: optNum(), plot_area_max: optNum(),
    carpet_area_min: optNum(), carpet_area_max: optNum(),
    budget_min: optNum("Budget must be a positive number"),
    budget_max: optNum("Budget must be a positive number"),
    budget_per_sqft_min: optNum(), budget_per_sqft_max: optNum(),
    condition_preference: optStr,
    bhk_requirement: optInt,
    furnishing_preference: optStr,
    floor_preference_min: optInt, floor_preference_max: optInt,
    parking_required: z.boolean().optional(),
    loan_assistance_needed: z.boolean().optional(),
    pre_leased_acceptable: z.boolean().optional(),
    amenities_required: commaArray,
    facing_direction_preference: optStr,
    reference_source: optStr,
    comments: optStr,
    declaration_signed: z.boolean().default(false),
    priority: z.string().min(1),
    status: z.string().min(1),
    next_follow_up_date: optStr,
    assigned_to: optStr,
    entry_date: optStr,
  })
  .refine((d) => d.budget_min != null || d.budget_max != null, {
    message: "At least one of budget min / max is required",
    path: ["budget_min"],
  });

const today = () => new Date().toISOString().slice(0, 10);

const blank = {
  enquiry_for: "", property_type_interested: "", property_subtype_interested: "",
  customer_name: "", customer_address: "", customer_phone_primary: "", customer_phone_secondary: "", customer_email: "", customer_dob: "", customer_occupation: "",
  preferred_areas: "", preferred_streets: "", city: "Kolhapur",
  saleable_area_min: "", saleable_area_max: "", plot_area_min: "", plot_area_max: "", carpet_area_min: "", carpet_area_max: "",
  budget_min: "", budget_max: "", budget_per_sqft_min: "", budget_per_sqft_max: "",
  condition_preference: "", bhk_requirement: "", furnishing_preference: "", floor_preference_min: "", floor_preference_max: "",
  parking_required: false, loan_assistance_needed: false, pre_leased_acceptable: false,
  amenities_required: "", facing_direction_preference: "",
  reference_source: "", comments: "", declaration_signed: false,
  priority: "Medium", status: "New", next_follow_up_date: "", assigned_to: "", entry_date: today(),
};

const toFormValues = (l) => ({
  ...blank,
  ...Object.fromEntries(Object.entries(l).map(([k, v]) => [k, v == null ? "" : v])),
  amenities_required: (l.amenities_required || []).join(", "),
  parking_required: !!l.parking_required,
  loan_assistance_needed: !!l.loan_assistance_needed,
  pre_leased_acceptable: !!l.pre_leased_acceptable,
  declaration_signed: !!l.declaration_signed,
});

export default function LeadFormPage() {
  const { id } = useParams();
  const isEdit = !!id;

  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => (await api.get(`/leads/${id}`)).data,
    enabled: isEdit,
  });

  if (isEdit && (isLoading || !lead)) return <div className="p-8 text-slate-400">Loading…</div>;
  return <LeadForm key={id || "new"} isEdit={isEdit} id={id} lead={lead} />;
}

function LeadForm({ isEdit, id, lead }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState("");

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });
  const staffOptions = useMemo(
    () => users.filter((u) => u.is_active).map((u) => ({ value: u.id, label: `${u.full_name} (${u.role})` })),
    [users]
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: isEdit ? toFormValues(lead) : blank,
  });
  const { handleSubmit, watch, setValue, formState: { isSubmitting } } = form;

  const propertyType = watch("property_type_interested");
  const isResidential = propertyType === "Residential";
  const isCommercial = propertyType === "Commercial";
  const subtypeOptions = useMemo(() => SUBTYPES[propertyType] || [], [propertyType]);

  const onSubmit = async (data) => {
    setServerError("");
    const payload = { ...data };
    Object.keys(payload).forEach((k) => payload[k] === undefined && (payload[k] = null));
    try {
      const saved = isEdit
        ? (await api.put(`/leads/${id}`, payload)).data
        : (await api.post("/leads", payload)).data;
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead", saved.id] });
      toast.success(isEdit ? "Lead updated" : "Lead added");
      navigate(`/leads/${saved.id}`);
    } catch (e) {
      setServerError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-5xl" data-testid="lead-form-page">
      <PageHeader title={isEdit ? "Edit Lead" : "Add Lead"} subtitle="Matches the paper Property Requirement form" />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Section title="Enquiry Info">
          <S form={form} prefix="lead" name="enquiry_for" label="Enquiry For" options={ENQUIRY_FOR} required />
          <S form={form} prefix="lead" name="property_type_interested" label="Property Type" options={PROPERTY_TYPES} required onValueChange={() => setValue("property_subtype_interested", "")} />
          <S form={form} prefix="lead" name="property_subtype_interested" label="Property Subtype" options={subtypeOptions} />
        </Section>

        <Section title="Client Info">
          <F form={form} prefix="lead" name="customer_name" label="Customer Name" required />
          <F form={form} prefix="lead" name="customer_phone_primary" label="Phone (Primary)" required placeholder="10-digit mobile" />
          <F form={form} prefix="lead" name="customer_phone_secondary" label="Phone (Secondary)" />
          <F form={form} prefix="lead" name="customer_email" label="Email" type="email" />
          <F form={form} prefix="lead" name="customer_dob" label="Date of Birth" type="date" />
          <F form={form} prefix="lead" name="customer_occupation" label="Occupation" />
          <T form={form} prefix="lead" name="customer_address" label="Customer Address" />
        </Section>

        <Section title="Location Preference">
          <T form={form} prefix="lead" name="preferred_areas" label="Preferred Areas / Localities (comma separated)" span="sm:col-span-2" />
          <F form={form} prefix="lead" name="preferred_streets" label="Preferred Streets" />
          <F form={form} prefix="lead" name="city" label="City" required />
        </Section>

        <Section title="Size Requirement (sqft)">
          <F form={form} prefix="lead" name="saleable_area_min" label="Saleable Area — Min" type="number" />
          <F form={form} prefix="lead" name="saleable_area_max" label="Saleable Area — Max" type="number" />
          <div className="hidden lg:block" />
          <F form={form} prefix="lead" name="carpet_area_min" label="Carpet Area — Min" type="number" />
          <F form={form} prefix="lead" name="carpet_area_max" label="Carpet Area — Max" type="number" />
          <div className="hidden lg:block" />
          <F form={form} prefix="lead" name="plot_area_min" label="Plot Area — Min" type="number" />
          <F form={form} prefix="lead" name="plot_area_max" label="Plot Area — Max" type="number" />
        </Section>

        <Section title="Budget">
          <F form={form} prefix="lead" name="budget_min" label="Budget — Min (₹)" type="number" />
          <F form={form} prefix="lead" name="budget_max" label="Budget — Max (₹)" type="number" />
          <div className="hidden lg:block" />
          <F form={form} prefix="lead" name="budget_per_sqft_min" label="Budget per sqft — Min (₹)" type="number" />
          <F form={form} prefix="lead" name="budget_per_sqft_max" label="Budget per sqft — Max (₹)" type="number" />
        </Section>

        <Section title="Preferences">
          <S form={form} prefix="lead" name="condition_preference" label="Condition" options={CONDITIONS} />
          {isResidential && <F form={form} prefix="lead" name="bhk_requirement" label="BHK Requirement" type="number" />}
          <S form={form} prefix="lead" name="furnishing_preference" label="Furnishing" options={FURNISHING} />
          <F form={form} prefix="lead" name="floor_preference_min" label="Floor Preference — Min" type="number" />
          <F form={form} prefix="lead" name="floor_preference_max" label="Floor Preference — Max" type="number" />
          <S form={form} prefix="lead" name="facing_direction_preference" label="Facing Preference" options={FACING} />
          <CB form={form} prefix="lead" name="parking_required" label="Parking Required" />
          <CB form={form} prefix="lead" name="loan_assistance_needed" label="Loan Assistance Needed" />
          {isCommercial && <CB form={form} prefix="lead" name="pre_leased_acceptable" label="Pre-leased Acceptable" />}
          <F form={form} prefix="lead" name="amenities_required" label="Amenities Required (comma separated)" span="sm:col-span-2" />
        </Section>

        <Section title="Additional Info">
          <F form={form} prefix="lead" name="reference_source" label="Reference Source" />
          <T form={form} prefix="lead" name="comments" label="Comments" />
          <CB form={form} prefix="lead" name="declaration_signed" label="Declaration signed & brokerage agreed" />
        </Section>

        <Section title="Tracking">
          <S form={form} prefix="lead" name="priority" label="Priority" options={LEAD_PRIORITIES} required />
          <S form={form} prefix="lead" name="status" label="Status" options={LEAD_STATUSES} />
          <F form={form} prefix="lead" name="next_follow_up_date" label="Next Follow-up Date" type="date" />
          <S form={form} prefix="lead" name="assigned_to" label="Assign To" options={[{ value: "__none__", label: "Unassigned" }, ...staffOptions]} />
          <F form={form} prefix="lead" name="entry_date" label="Entry Date" type="date" />
        </Section>

        {serverError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid="lead-form-error">{serverError}</p>
        )}

        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={isSubmitting}
            data-testid="lead-form-submit"
            className="rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white px-8 h-11 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md"
          >
            {isSubmitting ? "Saving…" : isEdit ? "Save Changes" : "Add Lead"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-lg h-11">Cancel</Button>
        </div>
      </form>
    </div>
  );
}
